<script setup lang="ts">
import type { SelectRootEmits } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import type { ControlSize, ControlWidth } from '@/components/base/_internal/control';
import { Loader } from 'lucide';
import { useForwardProps } from 'reka-ui';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { controlWidthClass, useControlSize, useControlWidth, useFieldControl } from '@/components/base/_internal/control';
import { SphIcon } from '@/components/base/icon/';
import { SphInput } from '@/components/base/input/';
import { SphTagGroup } from '@/components/base/tag-group/';
import { useDeferredRef } from '@/composables/deferred-ref';
import { useI18n } from '@/composables/i18n';
import { cn } from '@/utils/shadcn/lib/utils';
import Select from './internal/Select.vue';
import SelectContent from './internal/SelectContent.vue';
import SelectGroup from './internal/SelectGroup.vue';
import SelectItem from './internal/SelectItem.vue';
import SelectLabel from './internal/SelectLabel.vue';
import SelectTrigger from './internal/SelectTrigger.vue';
import SelectValue from './internal/SelectValue.vue';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
    inheritAttrs: false,
});

const props = withDefaults(defineProps<{
    class?: HTMLAttributes['class']
    options?: SelectOption[]
    placeholder?: string
    clearable?: boolean
    nullOption?: string | boolean
    multiple?: boolean | { maxItems?: false | number, hideSelected?: boolean, noTags?: boolean }
    searchable?: boolean
    id?: string
    /** Falls back to the size a container provides (see `SphControlProvider`), then to `md`. */
    size?: ControlSize
    /** Falls back to the width a container provides, then fills the container. `auto` follows the selected label. */
    width?: ControlWidth
    loading?: boolean
    disabled?: boolean
    required?: boolean
    invalid?: boolean
}>(), {
    class: '',
    options: () => [],
    placeholder: undefined,
    clearable: false,
    nullOption: '',
    multiple: false,
    searchable: false,
    id: undefined,
    size: undefined,
    width: undefined,
    loading: false,
    disabled: false,
    required: false,
    invalid: false,
});

defineEmits<SelectRootEmits>();

interface SelectOption {
    value: any
    label: string
    /** Heads a section of the list; consecutive options with the same group share one heading. */
    group?: string
    [key: string]: any
}

interface OptionSection {
    label: string | undefined
    options: SelectOption[]
}

const { i18n } = useI18n();

const size = useControlSize(() => props.size, 'md');
const width = useControlWidth(() => props.width, 'text');
const field = useFieldControl(() => props, () => width.value);

const nullOptionLabel = computed(() => {
    if (props.nullOption === false) {
        return null;
    }

    if (props.nullOption) {
        if (typeof props.nullOption === 'string') {
            return props.nullOption;
        }
    }
    return i18n.t('Keine Auswahl');
});

type SelectValueType = string | number;
const modelValue = defineModel<SelectValueType | SelectValueType[]>('modelValue');

const selectedOptions = computed(() => {
    const raw = modelValue.value;
    const values: SelectValueType[] = Array.isArray(raw)
        ? raw
        : (raw === undefined || raw === '' ? [] : [raw]);

    return props.options?.filter((option) => values.includes(option.value)) || [];
});

const remainingOptions = computed(() => {
    const selectedValues = selectedOptions.value.map((option) => option.value);
    return props.options?.filter((option) => !selectedValues.includes(option.value)) || [];
});

const search = ref('');

function computeVisible(): SelectOption[] {
    const hideSelected = typeof props.multiple === 'object' && props.multiple.hideSelected;
    let list = hideSelected ? remainingOptions.value : props.options;

    if (props.searchable && search.value.trim()) {
        const needle = search.value.trim().toLowerCase();
        list = list.filter((option) => option.label.toLowerCase().includes(needle));
    }

    return list;
}

const visibleOptions = ref(computeVisible());

const sections = computed<OptionSection[]>(() => {
    const result: OptionSection[] = [];
    for (const option of visibleOptions.value) {
        const last = result.at(-1);
        if (last !== undefined && last.label === option.group) {
            last.options.push(option);
        } else {
            result.push({ label: option.group, options: [option] });
        }
    }
    return result;
});

const showTags = computed(() => {
    return props.multiple && (typeof props.multiple !== 'object' || props.multiple.noTags !== true);
});

const placeholder = computed(() => {
    return props.placeholder !== undefined ? props.placeholder : i18n.t('Bitte auswählen');
});

const forwardedProps = computed(() => ({
    placeholder,
    multiple: !!props.multiple,
    disabled: field.value.disabled,
    required: field.value.required,
}));
const forwarded = useForwardProps(forwardedProps);

const isOpen = ref(false);
const suppressNextFocus = ref(false);

watch(isOpen, (val) => {
    if (!val) {
        suppressNextFocus.value = true;
        visibleOptions.value = computeVisible();
        search.value = '';
    }
}, { flush: 'sync' });

watch(search, () => {
    visibleOptions.value = computeVisible();
});

watch(remainingOptions, () => {
    if (!isOpen.value) {
        visibleOptions.value = computeVisible();
    }
});

function handleFocus() {
    if (suppressNextFocus.value) {
        suppressNextFocus.value = false;
        return;
    }

    isOpen.value = true;
}

// when the browser window/tab regains focus, it may re-fire a native `focus`
// event on the previously focused trigger before any user interaction lands.
// suppress that spurious focus so the menu doesn't pop open unexpectedly.
function handleWindowFocus() {
    suppressNextFocus.value = true;
}

onMounted(() => {
    window.addEventListener('focus', handleWindowFocus);
});

onUnmounted(() => {
    window.removeEventListener('focus', handleWindowFocus);
});

// loading state with start-delay to prevent flickering on fast operations
const internalLoading = useDeferredRef(() => props.loading, { delayOn: 600, delayOff: 0 });

// display
const displayValue = computed(() => {
    if (props.multiple) {
        const count = selectedOptions.value.length;
        if (!count) {
            return undefined;
        }
        return i18n.t('{count} ausgewählt', { count });
    } else {
        return selectedOptions.value[0]?.label || '';
    }
});

// tags representation for multiple select mode
const tags = computed(() =>
    selectedOptions.value.map((option) => ({ value: option.value, label: option.label })),
);

function deselectOption(tag: { value?: any }) {
    if (!props.multiple || !Array.isArray(modelValue.value)) {
        return;
    }

    const currentValue = modelValue.value || [];
    if (Array.isArray(currentValue)) {
        const newValue = currentValue.filter((v: any) => v !== tag.value);
        modelValue.value?.splice(0, modelValue.value.length, ...(newValue as any));
    }
}

function onSelect() {
}

function onClear() {
    if (props.multiple) {
        modelValue.value = [];
    } else {
        modelValue.value = '';
    }
}
</script>

<template>
    <div :class="cn('sph-select max-w-full', controlWidthClass(width), props.class)">
        <Select
            v-bind="forwarded"
            v-model:open="isOpen"
            :model-value="modelValue"
            @update:model-value="(val) => { modelValue = val === null ? (props.multiple ? [] : '') : (val as SelectValueType | SelectValueType[]) }"
        >
            <SelectTrigger
                :id="field.id"
                :has-clear-button="props.clearable"
                :size="size"
                :fill="width !== 'legacy' && width !== 'auto'"
                :show-clear-button="selectedOptions.length > 0"
                :aria-invalid="field.invalid ? 'true' : undefined"
                :aria-describedby="field.describedBy"
                :placeholder="placeholder"
                @focus="handleFocus"
                @clear="onClear"
            >
                <!--
                    Only the loader is animated, not the value making room for it: a transition-group
                    would measure both with `getBoundingClientRect()` on every render of the trigger —
                    a forced layout of the whole page each time the selection or the options change.
                -->
                <div class="flex items-center gap-2 min-w-0">
                    <transition name="smooth-list">
                        <SphIcon
                            v-if="internalLoading"
                            :icon="Loader"
                            size="sm"
                            class="[&>svg]:animate-[spin_1.25s_linear_infinite]"
                        />
                    </transition>
                    <SelectValue
                        class="select-none block flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
                        :placeholder="placeholder"
                    >
                        <template
                            v-if="displayValue"
                        >
                            <slot
                                name="display"
                                :options="selectedOptions"
                            >
                                {{ displayValue }}
                            </slot>
                        </template>
                    </SelectValue>
                </div>
            </SelectTrigger>
            <SelectContent>
                <div
                    v-if="searchable"
                    class="p-2 border-b border-border"
                >
                    <SphInput
                        v-model="search"
                        :placeholder="i18n.t('Suchen')"
                        clearable
                        size="sm"
                        @click.stop
                        @keydown.stop
                    />
                </div>
                <SelectItem
                    v-if="!props.multiple && nullOptionLabel"
                    :value="null"
                    class="text-muted-foreground"
                >
                    {{ nullOptionLabel }}
                </SelectItem>
                <SelectGroup
                    v-for="(section, index) in sections"
                    :key="section.label ?? index"
                >
                    <SelectLabel v-if="section.label !== undefined">
                        {{ section.label }}
                    </SelectLabel>
                    <SelectItem
                        v-for="option in section.options"
                        :key="option.value"
                        :value="option.value"
                        @select="onSelect"
                    >
                        <slot
                            name="option-label"
                            :option="option"
                        >
                            {{ option.label }}
                        </slot>
                    </SelectItem>
                </SelectGroup>
            </SelectContent>
        </Select>

        <SphTagGroup
            v-if="showTags"
            collapse-content-class="pt-2"
            :model-value="tags"
            :deletable="true"
            @delete="deselectOption"
        />
    </div>
</template>

<style scoped>
.smooth-list-enter-from {
    opacity: 0;
    transform: translate(50%, 0);
}

.smooth-list-leave-to {
    opacity: 0;
    transform: translate(0%, 0);
}

.smooth-list-enter-to,
.smooth-list-leave-from {
    opacity: 1;
    transform: translate(0, 0);
}

.smooth-list-enter-active,
.smooth-list-leave-active {
    transition: all 0.4s var(--ease-out-cubic);
}

.smooth-list-leave-active {
    position: absolute !important;
}
</style>
